/**
 * Ink helpers for the drawing surface, kept free of React and the DOM so they
 * can be unit-tested: pad units, stroke smoothing for rendering, and the
 * pointer policy (pen / touch / mouse arbitration with palm rejection).
 */
import type { HwPoint } from '../contracts.ts';

/**
 * Strokes are stored in "pad units": the pad's height is always PAD_UNITS,
 * whatever its size on screen. That keeps ink proportional when the pad is
 * resized (rotation, split view) and gives the recogniser a device-independent
 * scale for the size rules it can only apply to a lone mark (is this tiny
 * blob a decimal point?) — see docs/HANDWRITING.md.
 */
export const PAD_UNITS = 100;

/** Pen movements shorter than this (pad units) are dropped: sensor noise, not ink. */
export const MIN_POINT_GAP = 0.35;

/** Stroke width in CSS px for a pad of the given height: chunky, but never a blob. */
export const inkWidth = (padHeightPx: number): number => Math.min(10, Math.max(4, padHeightPx * 0.045));

export const pxToUnits = (px: number, padHeightPx: number): number => (padHeightPx > 0 ? (px * PAD_UNITS) / padHeightPx : 0);

export const unitsToPx = (units: number, padHeightPx: number): number => (units * padHeightPx) / PAD_UNITS;

/** Append a point unless it is closer than MIN_POINT_GAP to the previous one. Returns true when added. */
export function appendPoint(stroke: HwPoint[], p: HwPoint): boolean {
  const last = stroke[stroke.length - 1];
  if (last && Math.hypot(p.x - last.x, p.y - last.y) < MIN_POINT_GAP) return false;
  stroke.push(p);
  return true;
}

// ───────────── Smoothing ─────────────

export type PathOp =
  | { op: 'move'; x: number; y: number }
  | { op: 'line'; x: number; y: number }
  | { op: 'quad'; cx: number; cy: number; x: number; y: number }
  /** A tap or a stroke too short to have a direction: drawn as a filled dot. */
  | { op: 'dot'; x: number; y: number };

interface XY {
  x: number;
  y: number;
}

/**
 * Midpoint quadratic smoothing: each recorded point becomes the control point
 * of a curve between the midpoints on either side of it. The curve passes
 * through the first and last points, and corners soften without lagging
 * behind the pen the way averaging filters do.
 */
export function smoothPath(points: readonly XY[]): PathOp[] {
  const first = points[0];
  if (!first) return [];
  const last = points[points.length - 1]!;
  const moved = points.some((p) => Math.hypot(p.x - first.x, p.y - first.y) >= MIN_POINT_GAP);
  if (!moved) return [{ op: 'dot', x: first.x, y: first.y }];
  const ops: PathOp[] = [{ op: 'move', x: first.x, y: first.y }];
  for (let i = 1; i < points.length - 1; i++) {
    const p = points[i]!;
    const q = points[i + 1]!;
    ops.push({ op: 'quad', cx: p.x, cy: p.y, x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 });
  }
  ops.push({ op: 'line', x: last.x, y: last.y });
  return ops;
}

// ───────────── Pointer policy ─────────────

export type PointerKind = 'pen' | 'touch' | 'mouse';

/** PointerEvent.pointerType → kind; unknown or empty types behave like a mouse. */
export const pointerKind = (pointerType: string): PointerKind =>
  pointerType === 'pen' || pointerType === 'touch' ? pointerType : 'mouse';

/**
 * What to do with a new pointer: start a stroke, ignore it, or `replace` —
 * discard the stroke in progress (it was a palm or a resting finger) and
 * start this one instead.
 */
export type PointerDecision = 'start' | 'ignore' | 'replace';

/**
 * Decides which pointer is drawing. One stroke at a time; a pen always wins.
 *
 * Palm rejection: while a pen is down, and for PEN_GRACE_MS after any pen
 * activity (including hover on devices that report it), touch pointers are
 * treated as the writing hand resting on the glass and ignored. A touch
 * stroke already in progress when the pen lands is replaced by the pen
 * stroke. After the grace period fingers work again, so a player can put
 * the stylus down and carry on with a finger.
 */
export class PointerArbiter {
  static readonly PEN_GRACE_MS = 1000;

  private active: { id: number; kind: PointerKind } | null = null;
  private penSeenAt = -Infinity;

  constructor(private readonly penGraceMs = PointerArbiter.PEN_GRACE_MS) {}

  /** Record pen activity at `time` (ms, any monotonic clock). */
  notePen(time: number): void {
    this.penSeenAt = Math.max(this.penSeenAt, time);
  }

  down(id: number, kind: PointerKind, time: number): PointerDecision {
    if (kind === 'pen') {
      this.notePen(time);
      if (this.active?.kind === 'pen') return 'ignore';
      const decision = this.active ? 'replace' : 'start';
      this.active = { id, kind };
      return decision;
    }
    if (this.active) return 'ignore';
    if (kind === 'touch' && time - this.penSeenAt < this.penGraceMs) return 'ignore';
    this.active = { id, kind };
    return 'start';
  }

  /** True when `id` is the pointer currently drawing. */
  owns(id: number): boolean {
    return this.active?.id === id;
  }

  /** The pointer lifted (or was cancelled). Returns true when it was the drawing pointer. */
  up(id: number, time?: number): boolean {
    if (!this.owns(id)) return false;
    if (this.active?.kind === 'pen' && time !== undefined) this.notePen(time);
    this.active = null;
    return true;
  }

  /** Forget the stroke in progress (pad disabled or reset). */
  reset(): void {
    this.active = null;
  }
}
